import { useCallback, useEffect, useState } from 'react'
import { api, ApiError } from '../lib/api'
import type { GolfMember } from './useClubhouseBook'

/**
 * The signed-in member's own rounds — `GET /golf/rounds`, which returns every
 * round they kept score for OR played in, newest first, with the whole card
 * attached.
 *
 * This is the READ half of The Record. There is no history endpoint of its own
 * and there should not be: `rounds#index` already answers exactly this
 * question, it is already the surface the Caddie app reads, and a second
 * endpoint would be a second place for the scorekeeper-or-player scoping to
 * drift. The screen joins these to the member's outings on `outing_id`.
 *
 * Membership-gated (Fireside::MembershipGate), so a 403 `membership_required`
 * is the club's ANSWER and not an error — the same split `useClubhouseBook`
 * makes, and for the same reason: a clubhouse page stays open so a lapsed
 * member can always reach the account that fixes their dues.
 *
 * These shapes are declared here rather than in `lib/types.ts` deliberately,
 * exactly as `useClubhouseBook` declares the feed's. That file is copied
 * verbatim into the Caddie app by `bin/mobile contract`, and Caddie already
 * hand-authors its own round types (`apps/caddie/src/api/golf.ts`); promoting
 * these would start a migration that belongs to the app, not to this screen.
 */

/** One cell of one player's card (#hole_score_json). Sparse while a round is live. */
export interface HistoryHoleScore {
  hole_number: number
  strokes: number
  putts: number | null
  fairway_hit: boolean | null
}

/**
 * One player's line in a round (#round_player_json).
 *
 * `total_strokes` is null until there are cells to add up, and `guest` is what
 * says a name belongs to somebody with no account rather than a member who has
 * not answered. A member card that is neither accepted nor declined is still
 * awaiting its owner — the screen labels that rather than counting it.
 */
export interface HistoryPlayer {
  id: string
  user_id: string | null
  display_name: string
  guest: boolean
  position: number
  accepted_at: string | null
  declined_at: string | null
  course_handicap: number | null
  total_strokes: number | null
  score_differential: number | null
  scores: HistoryHoleScore[]
}

/** One hole of the course as carded (#course_hole_json). `par` may be null. */
export interface HistoryCourseHole {
  number: number
  par: number | null
  stroke_index: number | null
  yardage: number | null
}

/**
 * The course as a card (#golf_scorecard_json). An unseeded course carries
 * `holes: []` and `par: null` — legitimate, and the reason the screen never
 * prints a par it was not given.
 */
export interface HistoryCourse {
  id: string
  key: string
  name: string
  city: string | null
  holes_count: number
  par: number | null
  holes: HistoryCourseHole[]
}

/** A round as its own players see it (#round_json). */
export interface HistoryRound {
  id: string
  status: 'in_progress' | 'completed'
  holes_count: number
  starting_hole: number
  hole_numbers: number[]
  played_on: string | null
  completed_at: string | null
  posted_at: string | null
  /** The tee time this came out of, when it came out of one. */
  outing_id: string | null
  course: HistoryCourse
  scorekeeper: GolfMember | null
  players: HistoryPlayer[]
}

interface RoundsResponse {
  rounds: HistoryRound[]
}

export function useRoundHistory(enabled: boolean) {
  const [rounds, setRounds] = useState<HistoryRound[]>([])
  const [isLoading, setIsLoading] = useState(enabled)
  const [error, setError] = useState<string | null>(null)
  const [membershipRequired, setMembershipRequired] = useState(false)

  const refresh = useCallback(() => {
    if (!enabled) return Promise.resolve()
    return api
      .get<RoundsResponse>('/golf/rounds')
      .then((data) => {
        setRounds(data.rounds)
        setMembershipRequired(false)
        setError(null)
      })
      .catch((e: unknown) => {
        // Nothing is held from a previous read: a standing that just lapsed
        // must not leave a member's rounds on the page behind the gate.
        if (e instanceof ApiError && e.code === 'membership_required') {
          setRounds([])
          setMembershipRequired(true)
          setError(null)
          return
        }
        setError(e instanceof Error ? e.message : 'Could not open your record.')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [enabled])

  useEffect(() => {
    void refresh()
  }, [refresh])

  return {
    rounds,
    isLoading: enabled ? isLoading : false,
    error,
    membershipRequired,
    refresh,
  }
}
